/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode", "ObjectPropertyName", "Detekt:TooManyFunctions", "ClassName")
@file:KlangScript.Library("sprudel")

package io.peekandpoke.klang.sprudel.lang

import io.peekandpoke.klang.audio_bridge.AdsrCurves
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelVoiceData
import io.peekandpoke.klang.sprudel._applyControlFromParams
import io.peekandpoke.klang.sprudel._liftOrReinterpretNumericalField
import io.peekandpoke.klang.sprudel._mapNumericField
import io.peekandpoke.klang.sprudel.lang.SprudelDslArg.Companion.asSprudelDslArgs

// -- vibrato ---------------------------------------------------------------------------------------------------------

private val vibratoRateMutation = voiceSetter { vibrato = it?.asDoubleOrNull() ?: vibrato }

private fun applyVibratoRate(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.vibrato }, update = vibratoRateMutation)
    }

    return source._liftOrReinterpretNumericalField(args, vibratoRateMutation)
}

private val vibratoSemitonesMutation = voiceSetter { vibratoMod = it?.asDoubleOrNull() }

private fun applyVibratoSemitones(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.vibratoMod }, update = vibratoSemitonesMutation)
    }

    return source._liftOrReinterpretNumericalField(args, vibratoSemitonesMutation)
}

private val vibratoRangeFromMutation = voiceSetter { vibratoRangeFrom = it?.asDoubleOrNull() }

private fun applyVibratoRangeFrom(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.vibratoRangeFrom }, update = vibratoRangeFromMutation)
    }

    return source._liftOrReinterpretNumericalField(args, vibratoRangeFromMutation)
}

private val vibratoRangeToMutation = voiceSetter { vibratoRangeTo = it?.asDoubleOrNull() }

private fun applyVibratoRangeTo(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.vibratoRangeTo }, update = vibratoRangeToMutation)
    }

    return source._liftOrReinterpretNumericalField(args, vibratoRangeToMutation)
}

private val vibratoPhaseMutation = voiceSetter { vibratoPhase = it?.asDoubleOrNull() }

private fun applyVibratoPhase(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.vibratoPhase }, update = vibratoPhaseMutation)
    }

    return source._liftOrReinterpretNumericalField(args, vibratoPhaseMutation)
}

/**
 * Vibrato: LFO rate in Hz, depth in semitones, where the swing sits, and where the LFO starts.
 *
 * A pitch wobble on the note per voice: the rate is how fast, the depth
 * how far. A rate of 3 Hz is gentle, 5 standard, 7 nervous; a depth of 0.2 semitones is subtle,
 * 0.5 expressive, 1 a wide wobble.
 *
 * `rangeFrom` and `rangeTo` place the swing, in the -1..1 language of `range`: the LFO's -1 lands on `rangeFrom`, its
 * +1 on `rangeTo`, and the depth scales that. Unset, they are -1 and 1, a wobble both ways around the note;
 * `rangeFrom = 0, rangeTo = 1` swings only upward, as a guitarist bends a fretted string. Raw, no clamp: `rangeTo = 2`
 * swings twice the depth upward, `rangeFrom = 1, rangeTo = -1` turns the wobble upside down. `phase` is where in its
 * cycle the wobble starts, a fraction of one cycle: 0 (unset) in the middle of the swing, rising, which is the note
 * only for a swing centred on it (the default); 0.25 at its top; 0.5 in the middle, falling; 0.75 at its bottom. So an
 * upward-only vibrato starts on the note at `phase = 0.75` (at 0 it would start a quarter of its depth sharp), a
 * downward-only one at 0.25. It wraps (1.25 is 0.25).
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`vibrato(semitones = mul(2))`), and the numeric slots read back as `vibrato.rate`, `vibrato.semitones`,
 * `vibrato.rangeFrom`, `vibrato.rangeTo`, `vibrato.phase`. With no argument at all, the pattern's own values are
 * reinterpreted as `rate`.
 *
 * The door fills the vibrato stage of `classic()` (the `vibrato.*` slots), the Ignitor `vibrato` node, whose
 * `range(from, to)` and `phase` builder knobs these are; an instrument without `classic()` ignores it, like the other
 * `classic()` doors. The depth is the switch: a call without it (`vib(4)`, `vib(rangeFrom = 0)`) switches nothing on.
 *
 * ```KlangScript(Playable)
 * note("c4 e4").s("saw").vibrato(5, 0.5)                                  // a singing vibrato
 * ```
 *
 * ```KlangScript(Playable)
 * note("c4 e4").s("saw").vibrato(5, 0.5).vibrato(rate = mul("1 1.5"))     // the second note wobbles faster
 * ```
 *
 * ```KlangScript(Playable)
 * note("c4 e4").s("saw").vibrato(rate = 5, semitones = 0.5, rangeFrom = 0, rangeTo = 1, phase = 0.75)   // a guitar's vibrato: from the note, only ever up
 * ```
 *
 * ```KlangScript(Playable)
 * note("c4 e4").s("saw").vibrato(rate = 4, semitones = 0.7, phase = "0 0.5")   // the second note starts its wobble falling
 * ```
 *
 * ```KlangScript(Playable)
 * note("c4 e4").s("saw").vibrato("3 7", 0.5).penv(vibrato.rate)           // an onset blip as many semitones wide as the rate
 * ```
 *
 * @param rate LFO rate in Hz.
 * @param semitones Depth in semitones.
 * @param rangeFrom Where the LFO's -1 lands, in the -1..1 language of `range`, raw (default -1).
 * @param rangeTo Where the LFO's +1 lands, in the -1..1 language of `range`, raw (default 1).
 * @param phase Where the wobble starts, a fraction of one cycle; 0 is mid-swing, rising (default 0).
 *
 * @scope voice
 * @category tonal
 * @tags vibrato, rate, semitones, range, phase
 */
@KlangScript.Function
fun SprudelPattern.vibrato(
    rate: PatternLike? = null,
    semitones: PatternLike? = null,
    rangeFrom: PatternLike? = null,
    rangeTo: PatternLike? = null,
    phase: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern {
    // A tail-only call must not touch rate: reinterpret runs only on a fully bare call.
    var p = if (rate != null || !(semitones != null || rangeFrom != null || rangeTo != null || phase != null)) {
        applyVibratoRate(this, listOfNotNull(rate).asSprudelDslArgs(callInfo))
    } else {
        this
    }

    if (semitones != null) p = applyVibratoSemitones(p, listOf<Any?>(semitones).asSprudelDslArgs(callInfo?.forParam(1)))

    if (rangeFrom != null) {
        p = applyVibratoRangeFrom(p, listOf<Any?>(rangeFrom).asSprudelDslArgs(callInfo?.forParam(2)))
    }

    if (rangeTo != null) {
        p = applyVibratoRangeTo(p, listOf<Any?>(rangeTo).asSprudelDslArgs(callInfo?.forParam(3)))
    }

    if (phase != null) {
        p = applyVibratoPhase(p, listOf<Any?>(phase).asSprudelDslArgs(callInfo?.forParam(4)))
    }

    return p
}

/** Parses this string as a pattern, then applies [vibrato]. */
@KlangScript.Function
fun String.vibrato(
    rate: PatternLike? = null,
    semitones: PatternLike? = null,
    rangeFrom: PatternLike? = null,
    rangeTo: PatternLike? = null,
    phase: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).vibrato(
        rate = rate,
        semitones = semitones,
        rangeFrom = rangeFrom,
        rangeTo = rangeTo,
        phase = phase,
        callInfo = callInfo,
    )

/** Chains a [vibrato] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.vibrato(
    rate: PatternLike? = null,
    semitones: PatternLike? = null,
    rangeFrom: PatternLike? = null,
    rangeTo: PatternLike? = null,
    phase: PatternLike? = null,
    callInfo: CallInfo? = null,
): PatternMapperFn =
    this.chain { p ->
        p.vibrato(
            rate = rate,
            semitones = semitones,
            rangeFrom = rangeFrom,
            rangeTo = rangeTo,
            phase = phase,
            callInfo = callInfo,
        )
    }

/**
 * The `vibrato` object: `vibrato(...)` sets the slots, and each numeric slot reads back as a child,
 * `vibrato.rate`, `vibrato.semitones`, `vibrato.rangeFrom`, `vibrato.rangeTo`, `vibrato.phase`.
 *
 * @scope voice
 * @category tonal
 * @tags vibrato, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("vibrato")
object vibrato {

    /** The rate slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val rate: FieldAccessor = FieldAccessor { it.vibrato }

    /** The semitones slot (the depth) of each event, as a value other setters can read. */
    @KlangScript.Property
    val semitones: FieldAccessor = FieldAccessor { it.vibratoMod }

    /** The rangeFrom slot (where the LFO's -1 lands) of each event, as a value other setters can read. */
    @KlangScript.Property
    val rangeFrom: FieldAccessor = FieldAccessor { it.vibratoRangeFrom }

    /** The rangeTo slot (where the LFO's +1 lands) of each event, as a value other setters can read. */
    @KlangScript.Property
    val rangeTo: FieldAccessor = FieldAccessor { it.vibratoRangeTo }

    /** The phase slot (where the wobble starts, in cycles) of each event, as a value other setters can read. */
    @KlangScript.Property
    val phase: FieldAccessor = FieldAccessor { it.vibratoPhase }

    /**
     * The setter, see [SprudelPattern.vibrato].
     *
     * @param rate LFO rate in Hz.
     * @param semitones Depth in semitones.
     * @param rangeFrom Where the LFO's -1 lands, in the -1..1 language of `range`, raw (default -1).
     * @param rangeTo Where the LFO's +1 lands, in the -1..1 language of `range`, raw (default 1).
     * @param phase Where the wobble starts, a fraction of one cycle; 0 is mid-swing, rising (default 0).
     */
    @KlangScript.Invoke
    operator fun invoke(
        rate: PatternLike? = null,
        semitones: PatternLike? = null,
        rangeFrom: PatternLike? = null,
        rangeTo: PatternLike? = null,
        phase: PatternLike? = null,
        callInfo: CallInfo? = null,
    ): PatternMapperFn =
        { p ->
            p.vibrato(
                rate = rate,
                semitones = semitones,
                rangeFrom = rangeFrom,
                rangeTo = rangeTo,
                phase = phase,
                callInfo = callInfo,
            )
        }
}

/**
 * `vib`, the short name of [vibrato]: the same door, use whichever reads better.
 *
 * ```KlangScript(Playable)
 * note("c4 e4").s("saw").vib(5, 0.5)
 * ```
 *
 * @param rate LFO rate in Hz.
 * @param semitones Depth in semitones.
 * @param rangeFrom Where the LFO's -1 lands, in the -1..1 language of `range`, raw (default -1).
 * @param rangeTo Where the LFO's +1 lands, in the -1..1 language of `range`, raw (default 1).
 * @param phase Where the wobble starts, a fraction of one cycle; 0 is mid-swing, rising (default 0).
 *
 * @scope voice
 * @category tonal
 * @tags vib, vibrato
 */
@KlangScript.Function
fun SprudelPattern.vib(
    rate: PatternLike? = null,
    semitones: PatternLike? = null,
    rangeFrom: PatternLike? = null,
    rangeTo: PatternLike? = null,
    phase: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern =
    vibrato(
        rate = rate,
        semitones = semitones,
        rangeFrom = rangeFrom,
        rangeTo = rangeTo,
        phase = phase,
        callInfo = callInfo,
    )

/** Parses this string as a pattern, then applies [vib]. */
@KlangScript.Function
fun String.vib(
    rate: PatternLike? = null,
    semitones: PatternLike? = null,
    rangeFrom: PatternLike? = null,
    rangeTo: PatternLike? = null,
    phase: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern =
    this.vibrato(
        rate = rate,
        semitones = semitones,
        rangeFrom = rangeFrom,
        rangeTo = rangeTo,
        phase = phase,
        callInfo = callInfo,
    )

/**
 * Alias of [vibrato]: the same object under its short name.
 *
 * @scope voice
 * @category tonal
 * @tags vib, vibrato, accessor
 */
@KlangScript.Constant
val vib: vibrato = vibrato

/** Chains a [vib] step onto this [PatternMapperFn] (see [SprudelPattern.vib]). */
@KlangScript.Function
fun PatternMapperFn.vib(
    rate: PatternLike? = null,
    semitones: PatternLike? = null,
    rangeFrom: PatternLike? = null,
    rangeTo: PatternLike? = null,
    phase: PatternLike? = null,
    callInfo: CallInfo? = null,
): PatternMapperFn =
    this.vibrato(
        rate = rate,
        semitones = semitones,
        rangeFrom = rangeFrom,
        rangeTo = rangeTo,
        phase = phase,
        callInfo = callInfo,
    )

// -- penv ------------------------------------------------------------------------------------------------------------

private val penvSemitonesMutation = voiceSetter { pEnv = it?.asDoubleOrNull() ?: pEnv }

private fun applyPenvSemitones(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.pEnv }, update = penvSemitonesMutation)
    }

    return source._liftOrReinterpretNumericalField(args, penvSemitonesMutation)
}

private val penvAttackMutation = voiceSetter { pAttack = it?.asDoubleOrNull() }

private fun applyPenvAttack(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.pAttack }, update = penvAttackMutation)
    }

    return source._liftOrReinterpretNumericalField(args, penvAttackMutation)
}

private val penvDecayMutation = voiceSetter { pDecay = it?.asDoubleOrNull() }

private fun applyPenvDecay(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.pDecay }, update = penvDecayMutation)
    }

    return source._liftOrReinterpretNumericalField(args, penvDecayMutation)
}

private val penvSustainMutation = voiceSetter { pSustain = it?.asDoubleOrNull() }

private fun applyPenvSustain(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.pSustain }, update = penvSustainMutation)
    }

    return source._liftOrReinterpretNumericalField(args, penvSustainMutation)
}

private val penvReleaseMutation = voiceSetter { pRelease = it?.asDoubleOrNull() }

private fun applyPenvRelease(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.pRelease }, update = penvReleaseMutation)
    }

    return source._liftOrReinterpretNumericalField(args, penvReleaseMutation)
}

/**
 * The pitch envelope: its depth in semitones, and its attack, decay, sustain and release.
 *
 * The pitch rises `semitones` away from the note over `attack`, falls back to the `sustain`
 * share of `semitones` over `decay`, holds there while the note is on, and after the note ends returns
 * to the note over `release`. 12 is an octave up, -12 an octave down, 0 no pitch envelope at all.
 * An unwritten stage is the Ignitor `pitchEnvelope`'s default: attack 0.01, decay 0.1, sustain 0
 * (back on the note), release 0 (on the note at the note's end). Each stage bends exponentially
 * unless [penvCurves] shapes it. The release does not make the note ring longer.
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`penv(attack = mul(2))`), and the slots read back as `penv.semitones`, `penv.attack`,
 * `penv.decay`, `penv.sustain`, `penv.release`. With no argument at all, the pattern's own values
 * are reinterpreted as `semitones`.
 *
 * The door fills the pitch envelope stage of `classic()` (the `penv.*` slots), the Ignitor `pitchEnvelope` node; an
 * instrument without `classic()` ignores it, like the other `classic()` doors.
 *
 * ```KlangScript(Playable)
 * note("c2*4").s("sine").penv(24, 0.001, 0.08)                               // a kick: two octaves down onto the note
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3 ~ e3 ~").s("saw").penv(12, 0.02, 0.1, 0.5, 0.3).adsr(0.01, 0.1, 0.8, 0.5)   // holds half an octave up, glides home after
 * ```
 *
 * ```KlangScript(Playable)
 * note("c2*4").s("sine").penv(24, 0.001, 0.08).penv(semitones = mul("1 0.5"))   // half the drop on every second hit
 * ```
 *
 * ```KlangScript(Playable)
 * note("c4*4").s("saw").penv("12 -12", 0.01, 0.2).lpf(penv.semitones.mul(100).add(2000))   // brighter with the rise
 * ```
 *
 * @param semitones The pitch at the envelope's peak, in semitones.
 * @param attack Attack in seconds.
 * @param decay Decay in seconds.
 * @param sustain Held share of `semitones` while the note is on; 0 is the note itself.
 * @param release Release in seconds, back to the note after the note ends.
 *
 * @scope voice
 * @category tonal
 * @tags penv, semitones, attack, decay, sustain, release, pitch envelope
 */
@KlangScript.Function
fun SprudelPattern.penv(
    semitones: PatternLike? = null,
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    sustain: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    // A tail-only call must not touch semitones: reinterpret runs only on a fully bare call.
    var p = if (semitones != null || !(attack != null || decay != null || sustain != null || release != null)) {
        applyPenvSemitones(this, listOfNotNull(semitones).asSprudelDslArgs(callInfo))
    } else {
        this
    }

    if (attack != null) p = applyPenvAttack(p, listOf<Any?>(attack).asSprudelDslArgs(callInfo?.forParam(1)))
    if (decay != null) p = applyPenvDecay(p, listOf<Any?>(decay).asSprudelDslArgs(callInfo?.forParam(2)))
    if (sustain != null) p = applyPenvSustain(p, listOf<Any?>(sustain).asSprudelDslArgs(callInfo?.forParam(3)))
    if (release != null) p = applyPenvRelease(p, listOf<Any?>(release).asSprudelDslArgs(callInfo?.forParam(4)))

    return p
}

/** Parses this string as a pattern, then applies [penv]. */
@KlangScript.Function
fun String.penv(
    semitones: PatternLike? = null,
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    sustain: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).penv(semitones, attack, decay, sustain, release, callInfo)

/** Chains a [penv] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.penv(
    semitones: PatternLike? = null,
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    sustain: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.penv(semitones, attack, decay, sustain, release, callInfo) }

/**
 * The `penv` object: `penv(...)` sets the slots, and each slot reads back as a child,
 * `penv.semitones`, `penv.attack`, `penv.decay`, `penv.sustain`, `penv.release`.
 *
 * @scope voice
 * @category tonal
 * @tags penv, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("penv")
object penv {

    /** The semitones slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val semitones: FieldAccessor = FieldAccessor { it.pEnv }

    /** The attack slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val attack: FieldAccessor = FieldAccessor { it.pAttack }

    /** The decay slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val decay: FieldAccessor = FieldAccessor { it.pDecay }

    /** The sustain slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val sustain: FieldAccessor = FieldAccessor { it.pSustain }

    /** The release slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val release: FieldAccessor = FieldAccessor { it.pRelease }

    /**
     * The setter, see [SprudelPattern.penv].
     *
     * @param semitones The pitch at the envelope's peak, in semitones.
     * @param attack Attack in seconds.
     * @param decay Decay in seconds.
     * @param sustain Held share of `semitones` while the note is on; 0 is the note itself.
     * @param release Release in seconds, back to the note after the note ends.
     */
    @KlangScript.Invoke
    operator fun invoke(
        semitones: PatternLike? = null,
        attack: PatternLike? = null,
        decay: PatternLike? = null,
        sustain: PatternLike? = null,
        release: PatternLike? = null,
        callInfo: CallInfo? = null
    ): PatternMapperFn =
        { p -> p.penv(semitones, attack, decay, sustain, release, callInfo) }
}

// -- penvCurves ------------------------------------------------------------------------------------------------------

// The curve names have ONE home, `AdsrCurves` in audio_bridge; the rule is `adsrCurves`': an unknown
// name, like an omitted stage, keeps the stage's current curve.
private val penvAttackCurveMutation = voiceSetter {
    pAttackCurve = AdsrCurves.curveOf(it?.toString()) ?: pAttackCurve
}

private val penvDecayCurveMutation = voiceSetter {
    pDecayCurve = AdsrCurves.curveOf(it?.toString()) ?: pDecayCurve
}

private val penvReleaseCurveMutation = voiceSetter {
    pReleaseCurve = AdsrCurves.curveOf(it?.toString()) ?: pReleaseCurve
}

private fun applyPenvAttackCurve(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, penvAttackCurveMutation) { src, ctrl ->
        src.pAttackCurve = ctrl.pAttackCurve ?: src.pAttackCurve
        src
    }
}

private fun applyPenvDecayCurve(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, penvDecayCurveMutation) { src, ctrl ->
        src.pDecayCurve = ctrl.pDecayCurve ?: src.pDecayCurve
        src
    }
}

private fun applyPenvReleaseCurve(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, penvReleaseCurveMutation) { src, ctrl ->
        src.pReleaseCurve = ctrl.pReleaseCurve ?: src.pReleaseCurve
        src
    }
}

/**
 * Sets the shape of each stage of the pitch envelope ([penv]), the way [adsrCurves] shapes the
 * amplitude envelope. Each stage is independent; an omitted stage, or an unknown curve name, keeps
 * its current curve, so `penvCurves(decay = "linear")` changes only the decay.
 *
 * The curves are the same six as `adsrCurves`: `linear`, `square`, `cube`, `scurve`, `invsquare`,
 * `exponential`, with the same aliases. Unset, every stage is `exponential`, the default of every
 * modulation envelope on every surface (decision D3). A curve alone switches nothing on: without a
 * `penv` semitones written there is no pitch envelope to shape.
 *
 * ```KlangScript(Playable)
 * note("c2*4").s("sine").penv(24, 0.001, 0.08).penvCurves(decay = "linear")   // a straight drop, the old sweep
 * ```
 *
 * @param attack Curve name for the attack stage.
 * @param decay Curve name for the decay stage.
 * @param release Curve name for the release stage.
 *
 * @scope voice
 * @category tonal
 * @tags penv, curve, envelope, shape, pitch envelope
 */
@KlangScript.Function
fun SprudelPattern.penvCurves(
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern {
    var p = this
    if (attack != null) p = applyPenvAttackCurve(p, listOf<Any?>(attack).asSprudelDslArgs(callInfo?.forParam(0)))
    if (decay != null) p = applyPenvDecayCurve(p, listOf<Any?>(decay).asSprudelDslArgs(callInfo?.forParam(1)))
    if (release != null) p = applyPenvReleaseCurve(p, listOf<Any?>(release).asSprudelDslArgs(callInfo?.forParam(2)))
    return p
}

/**
 * Parses this string as a pattern and sets the pitch envelope's stage curves (see [penvCurves]).
 *
 * @param attack Curve name for the attack stage.
 * @param decay Curve name for the decay stage.
 * @param release Curve name for the release stage.
 */
@KlangScript.Function
fun String.penvCurves(
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).penvCurves(attack, decay, release, callInfo)

/**
 * The `penvCurves` object: `penvCurves(attack, decay, release)` sets the pitch envelope's stage curves
 * by name. The slots are names, not numbers, so the object carries the setter only and no readers
 * (the `adsrCurves` rule).
 *
 * ```KlangScript(Playable)
 * note("c2*4").s("sine").penv(24, 0.001, 0.08).apply(penvCurves("linear", "linear", "linear"))
 * ```
 *
 * @scope voice
 * @category tonal
 * @tags penv, curve, envelope, shape, pitch envelope
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("penvCurves")
object penvCurves {

    /**
     * The setter, see [SprudelPattern.penvCurves].
     *
     * @param attack Curve name for the attack stage.
     * @param decay Curve name for the decay stage.
     * @param release Curve name for the release stage.
     */
    @KlangScript.Invoke
    operator fun invoke(
        attack: PatternLike? = null,
        decay: PatternLike? = null,
        release: PatternLike? = null,
        callInfo: CallInfo? = null,
    ): PatternMapperFn = { p -> p.penvCurves(attack, decay, release, callInfo) }
}

/**
 * Creates a chained [PatternMapperFn] that sets the pitch envelope's stage curves after the previous mapper.
 *
 * @param attack Curve name for the attack stage. Omit to keep the current curve.
 * @param decay Curve name for the decay stage. Omit to keep the current curve.
 * @param release Curve name for the release stage. Omit to keep the current curve.
 */
@KlangScript.Function
fun PatternMapperFn.penvCurves(
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null,
): PatternMapperFn =
    this.chain { p -> p.penvCurves(attack, decay, release, callInfo) }

// -- accelerate() -----------------------------------------------------------------------------------------------------

private val accelerateUpdate: SprudelVoiceData.(Double?) -> SprudelVoiceData = { semitones ->
    clone().also { it.accelerate = semitones }
}

private fun applyAccelerate(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.accelerate }, update = accelerateUpdate)
    }

    return source._liftOrReinterpretNumericalField(args, accelerateUpdate)
}

/**
 * Sets the playback acceleration (pitch ramp) for each event, in SEMITONES over the event's
 * duration: `accelerate(12)` glides one octave up, `accelerate(-12)` one octave down. The glide
 * runs from the onset to the end of the note (the gate) and holds its target through the release.
 *
 * A continuous pitch change during sample playback, good for pitched percussion and sweeps. With
 * no argument it reinterprets the current event value as the semitone amount. (The unit changed
 * from octaves to semitones in the pitch-param unification, 2026-08-24, so old scripts' values are
 * 12× subtler now.)
 *
 * The door fills the accelerate stage of `classic()` (the flat `accelerate` slot), the Ignitor `accelerate` node; an
 * instrument without `classic()` ignores it, like the other `classic()` doors.
 *
 * ```KlangScript(Playable)
 * s("cr").accelerate(24)             // crash pitches two octaves up during playback
 * ```
 *
 * ```KlangScript(Playable)
 * s("hh").accelerate("<0 -24 24>")   // alternate: no ramp, down, up per cycle
 * ```
 *
 * @param semitones Pitch bend in semitones. Typically -24 to 24.
 *
 * @scope voice
 * @category tonal
 * @tags accelerate, pitch ramp, pitch bend, playback speed
 */
@KlangScript.Function
fun SprudelPattern.accelerate(semitones: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    applyAccelerate(this, listOfNotNull(semitones).asSprudelDslArgs(callInfo))

/** Sets the playback acceleration on a string pattern. */
@KlangScript.Function
fun String.accelerate(semitones: PatternLike? = null, callInfo: CallInfo? = null): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).accelerate(semitones, callInfo)

/**
 * The pitch ramp of each event in semitones, as a value other setters can read.
 *
 * Bare `accelerate` reads what the chain has set so far, so it comes after whatever set the field
 * (`accelerate(...)` or an alias). Call it, `accelerate(...)`, to set the field; a mapper argument applies
 * to the field.
 *
 * ```KlangScript(Playable)
 * s("bd*4").accelerate(-2).accelerate(mul("1 2 1 4"))                       // deeper drops on every second hit
 * ```
 *
 * ```KlangScript(Playable)
 * note("c4 e4").accelerate("2 -2").pan(accelerate.mul(0.25).add(0.5))    // up goes right, down goes left
 * ```
 *
 * @scope voice
 * @category tonal
 * @tags accelerate, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("accelerate")
object accelerate : FieldAccessor({ it.accelerate }) {

    /**
     * Returns a [PatternMapperFn] that sets the playback acceleration (pitch ramp).
     * When called with no argument, reinterprets the current event value as an acceleration amount.
     *
     * ```KlangScript(Playable)
     * s("hh").apply(accelerate(24))  // mapper form
     * ```
     *
     * @param semitones Pitch bend in semitones. Typically -24 to 24.
     */
    @KlangScript.Invoke
    operator fun invoke(semitones: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
        { p -> p.accelerate(semitones, callInfo) }
}

/** Chains an accelerate operation onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.accelerate(semitones: PatternLike? = null, callInfo: CallInfo? = null): PatternMapperFn =
    this.chain { p -> p.accelerate(semitones, callInfo) }
